import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Eye, EyeOff } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useSpinner } from '@/hooks/useSpinner';
import { loginService } from '@/services/login.service';
import { franchiseService } from '@/services/franchise.service';
import { userService } from '@/services/user.service';
import { multiNivelService } from '@/services/multi-nivel.service';
import { telegramLogService } from '@/services/telegram-log.service';
import { getSubdomainFranchise } from '@/utils/get-subdomain';
import { estados } from '@/data/estados-cidades';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { navigateToRoute } from '@/utils/environment-urls';
import { useAuth } from '@/hooks/useAuth';

interface Franchise {
  subdomain: string;
  name: string;
  status: string;
}

interface FormData {
  nome_razao_social: string;
  cpf_cnpj: string;
  email: string;
  telefone: string;
  estado: string;
  cidade: string;
  password: string;
  aceite_termos: boolean;
}

const SideRegister = () => {
  const { isLoading, showSpinner, hideSpinner } = useSpinner();
  const [franquiasDisponiveis, setFranquiasDisponiveis] = useState<Franchise[]>([]);
  const [franquiaSelecionada, setFranquiaSelecionada] = useState<string | null>(null);
  const navigate = useNavigate();
  const isProduction = import.meta.env.PROD;
  
  // Estados do formulário
  const [showDocumentCheck, setShowDocumentCheck] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [documentCheck, setDocumentCheck] = useState('');
  const [cidades, setCidades] = useState<string[]>([]);
  const [cidadesFiltradas, setCidadesFiltradas] = useState<string[]>([]);
  const [pesquisaCidade, setPesquisaCidade] = useState('');
  const [mostrarDropdownCidades, setMostrarDropdownCidades] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Estados para código de indicação
  const [codigoIndicacao, setCodigoIndicacao] = useState('');
  const [nomeIndicante, setNomeIndicante] = useState('');
  const [codigoIndicacaoValido, setCodigoIndicacaoValido] = useState<boolean | null>(null);
  const [indicadorFromCode, setIndicadorFromCode] = useState('');
  const [verificandoCodigo, setVerificandoCodigo] = useState(false);
  
  // Estados para fluxo de indicação
  const [mostrarPerguntaIndicacao, setMostrarPerguntaIndicacao] = useState(true);
  const [usuarioTemIndicacao, setUsuarioTemIndicacao] = useState<boolean | null>(null);
  const [mostrarFormIndicacao, setMostrarFormIndicacao] = useState(false);
  
  // Debounce timer
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Formulário
  const [formData, setFormData] = useState<FormData>({
    nome_razao_social: '',
    cpf_cnpj: '',
    email: '',
    telefone: '',
    estado: '',
    cidade: '',
    password: '',
    aceite_termos: false,
  });

  const { login } = useAuth();
  const [errorLoadFranchises, setErrorLoadFranchises] = useState(false);

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    let franchiseSubdomain = getSubdomainFranchise();
    setFranquiaSelecionada(franchiseSubdomain);
    showSpinner();

    try {
      const franchises = await franchiseService.findAllAvailable();
      setFranquiasDisponiveis(franchises);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar dados. Tente novamente ou contate o suporte.');
      setErrorLoadFranchises(true);
    } finally {
      hideSpinner();
    }
  };

  const onFranquiaChange = (franquia: string) => {
    if (!franquia) return;

    setFranquiaSelecionada(franquia);

    if (franquia !== getSubdomainFranchise()) {
      showSpinner();

      const urlFranquia = isProduction 
        ? `https://${franquia}.xdoinfinitto.com.br/v2/authentication/side-register`
        : `http://${franquia ? franquia + "." : ""}localhost:8080/v2/authentication/side-register`;
      const urlMatriz = isProduction 
        ? `https://xdoinfinitto.com.br/v2/authentication/side-register`
        : `http://localhost:8080/v2/authentication/side-register`;

      if (franquia === 'matriz') {
        window.location.href = urlMatriz;
      } else {
        window.location.href = urlFranquia;
      }
    }
  };

  const aplicarMascaraCPFCNPJ = (value: string): string => {
    const clean = value.replace(/\D/g, '');
    
    if (clean.length <= 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const aplicarMascaraTelefone = (value: string): string => {
    const clean = value.replace(/\D/g, '');
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const getCidades = (estadoSigla: string) => {
    const estado = estados.find((e: any) => e.sigla === estadoSigla);
    if (estado) {
      setCidades(estado.cidades);
      setCidadesFiltradas(estado.cidades);
      setPesquisaCidade('');
      setMostrarDropdownCidades(false);
    } else {
      setCidades([]);
      setCidadesFiltradas([]);
      setPesquisaCidade('');
      setMostrarDropdownCidades(false);
    }
  };

  const handlePesquisaCidade = (termo: string) => {
    setPesquisaCidade(termo);
    const filtradas = cidades.filter((cidade) =>
      cidade.toLowerCase().includes(termo.toLowerCase())
    );
    setCidadesFiltradas(filtradas);
    setMostrarDropdownCidades(true);
  };

  const handleSelecionarCidade = (cidade: string) => {
    handleFormChange('cidade', cidade);
    setPesquisaCidade(cidade);
    setMostrarDropdownCidades(false);
  };

  const handleFormChange = (field: keyof FormData, value: any) => {
    if (field === 'cpf_cnpj') {
      value = aplicarMascaraCPFCNPJ(value);
    } else if (field === 'telefone') {
      value = aplicarMascaraTelefone(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'estado') {
      getCidades(value);
      setFormData(prev => ({
        ...prev,
        cidade: ''
      }));
    }
  };

  const handleDocumentCheckChange = (value: string) => {
    const clean = value.replace(/\D/g, '');
    const formatted = aplicarMascaraCPFCNPJ(clean);
    setDocumentCheck(formatted);
  };

  const checkPreRegister = async () => {
    if (!documentCheck || documentCheck.length < 14) {
      toast.error('Por favor, informe um CPF ou CNPJ válido');
      return;
    }

    showSpinner();
    try {
      const response = await userService.checkPreRegister(documentCheck);
      
      if (response.statusCode === 200) {
        const preRegisterData = response.data;
        
        // Se houver dados de pré-cadastro com situação definida
        if (preRegisterData && preRegisterData.situacao) {
          if (checkSituation(preRegisterData.situacao, preRegisterData.existente)) {
            hideSpinner();
            return;
          }

          // Verificar se email já existe
          if (preRegisterData.existente === 'email_existente') {
            hideSpinner();
            Swal.fire({
              icon: 'info',
              title: 'E-mail Existente',
              text: `Por favor, informe um e-mail diferente de: ${preRegisterData.email}`,
              confirmButtonText: 'Ok',
              showCancelButton: true,
              cancelButtonText: 'Cancelar',
            });
            return;
          }

          // Verificar se usuário já tem conta
          if (preRegisterData.user_id) {
            hideSpinner();
            Swal.fire({
              icon: 'info',
              title: 'Conta Existente',
              text: 'Você já realizou o cadastro, faça login',
              confirmButtonText: 'Fazer login',
            }).then(() => {
              navigate('/authentication/side-login');
            });
            return;
          }

          // Preencher formulário com dados do pré-cadastro
          setFormData(prev => ({
            ...prev,
            cpf_cnpj: documentCheck,
            nome_razao_social: preRegisterData.nome_razao_social || preRegisterData.fullName || '',
            email: preRegisterData.email || '',
            telefone: preRegisterData.telefone || preRegisterData.phone || '',
            estado: preRegisterData.estado || '',
            cidade: preRegisterData.cidade || '',
          }));

          if (preRegisterData.estado) {
            getCidades(preRegisterData.estado);
          }
        } else {
          // Nenhum dado pré-preenchido encontrado - prosseguir com formulário vazio
          setFormData(prev => ({
            ...prev,
            cpf_cnpj: documentCheck,
          }));
        }

        setShowDocumentCheck(false);
        setShowForm(true);
        hideSpinner();
      } else {
        hideSpinner();
        toast.error('Erro ao verificar pré-cadastro');
      }
    } catch (error: any) {
      hideSpinner();
      console.error('Erro ao verificar pré-cadastro:', error);
      toast.error('Erro ao verificar pré-cadastro');
    }
  };

  const checkSituation = (situacao: string, existente: string): boolean => {
    if (situacao === 'pendente' || situacao === 'analisando') {
      Swal.fire({
        icon: 'info',
        title: 'Pendente',
        text: 'Seus dados estão pendentes de análise. Por favor aguarde, entraremos em contato.',
        confirmButtonText: 'Ok'
      });
      return true;
    }

    if (situacao === 'reprovado') {
      Swal.fire({
        icon: 'info',
        title: 'Reprovado',
        text: 'Seu cadastro foi reprovado. Por favor, entre em contato em caso de dúvidas.',
        confirmButtonText: 'Ok'
      });
      return true;
    }

    if (existente === 'cpf_existente') {
      Swal.fire({
        icon: 'info',
        title: 'CPF Existente',
        text: 'Já existe um cadastro com este CPF. Por favor, entre em contato.',
        confirmButtonText: 'Entrar em contato',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          window.open('https://wa.me/45991559222', '_blank');
        }
      });
      return true;
    }

    return false;
  };

  const verificarCodigoIndicacao = async (codigo: string) => {
    if (!codigo || codigo.length === 0) {
      setCodigoIndicacaoValido(null);
      setNomeIndicante('');
      setIndicadorFromCode('');
      return;
    }

    setVerificandoCodigo(true);

    try {
      const response = await multiNivelService.getIndicantePorCodigo(codigo);

      if (response.success && response.data) {
        setCodigoIndicacaoValido(true);
        setNomeIndicante(response.data.nome);
        setIndicadorFromCode(codigo);
      } else {
        setCodigoIndicacaoValido(false);
        setNomeIndicante('');
        setIndicadorFromCode('');
        toast.error(response.message || 'Código de indicação não encontrado');
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      setCodigoIndicacaoValido(false);
      setNomeIndicante('');
      setIndicadorFromCode('');
      toast.error('Erro ao verificar código de indicação');
    } finally {
      setVerificandoCodigo(false);
    }
  };

  const onCodigoIndicacaoChange = (codigo: string) => {
    setCodigoIndicacao(codigo);

    if (codigo.length === 0) {
      setCodigoIndicacaoValido(null);
      setNomeIndicante('');
      setIndicadorFromCode('');
      return;
    }

    if (codigo.length < 3) {
      setCodigoIndicacaoValido(null);
      setNomeIndicante('');
      setIndicadorFromCode('');
      return;
    }

    // Reset temporário e usar debounce
    setCodigoIndicacaoValido(null);
    setNomeIndicante('');
    setIndicadorFromCode('');

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      verificarCodigoIndicacao(codigo);
    }, 800);
  };

  const handleRespostaPerguntaIndicacao = (resposta: boolean) => {
    setUsuarioTemIndicacao(resposta);
    setMostrarPerguntaIndicacao(false);
    
    if (resposta) {
      setMostrarFormIndicacao(true);
    } else {
      // Usuário respondeu não
      setMostrarFormIndicacao(false);
      setCodigoIndicacao('');
      setCodigoIndicacaoValido(null);
      setNomeIndicante('');
      setIndicadorFromCode('');
    }
  };

  const abrirFormIndicacaoNovamente = () => {
    setUsuarioTemIndicacao(true);
    setMostrarFormIndicacao(true);
  };

  const fecharIndicacao = () => {
    setMostrarPerguntaIndicacao(false);
    setMostrarFormIndicacao(false);
    setCodigoIndicacao('');
    setCodigoIndicacaoValido(null);
    setNomeIndicante('');
    setIndicadorFromCode('');
  };

  const validateForm = (): boolean => {
    if (!formData.nome_razao_social || !formData.cpf_cnpj || !formData.email || 
        !formData.telefone || !formData.estado || !formData.cidade || !formData.password) {
      toast.error('Por favor, preencha todos os campos');
      return false;
    }

    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return false;
    }

    if (!formData.aceite_termos) {
      toast.error('Por favor, aceite os termos de uso');
      return false;
    }

    if (formData.nome_razao_social.indexOf(' ') === -1) {
      toast.error('Por favor, insira um sobrenome');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    showSpinner();

    try {
      const logData = { errorMessage: '', registerData: {} };
      const codigoIndicador = indicadorFromCode || undefined;

      const response = await userService.firstAccess(formData, franquiaSelecionada || '', codigoIndicador);

      if (response.success || response.statusCode === 201) {
        await telegramLogService.send(
          true,
          `---   ✔ NOVO PRIMEIRO ACESSO 📋:   ---\nPrimeiro acesso realizado com sucesso. Dados: \n <pre>${JSON.stringify({
            nome: formData.nome_razao_social,
            cpf_cnpj: formData.cpf_cnpj,
            email: formData.email,
            telefone: formData.telefone,
            estado: formData.estado,
            cidade: formData.cidade,
            senha: '*******',
          })}</pre>\nIndicação Informada: ${codigoIndicador ? codigoIndicador + ' - ' + nomeIndicante : 'Não informado'}\n---   ✔   ---`,
          new Date()
        );

        await Swal.fire({
          icon: 'success',
          title: 'Tudo certo!',
          text: response.message || 'Cadastro realizado com sucesso!',
          confirmButtonText: 'Ok'
        });

        // Tentar fazer login automaticamente
        showSpinner();
        const loginOutput = await loginService.checkusernameandpassword(
          formData.email.trim().toLowerCase(),
          formData.password,
          franquiaSelecionada || ''
        );
        const userData = loginOutput.userData;

        hideSpinner();

        if (userData && userData.email && userData.role != 'pendente' && response.message.indexOf('aprovado') >= 0) {
          const nomeFranquia = franquiasDisponiveis.find(
            f => f.subdomain === franquiaSelecionada
          )?.name || null;

          if (nomeFranquia) {
            localStorage.setItem('franquia', nomeFranquia);
          }

          login(loginOutput.userData, loginOutput.idToken, loginOutput.refreshToken);
          
          if (loginOutput.userData.role === 'admin') {
            navigateToRoute('/painel/home');
          } else if (loginOutput.userData.role === 'revendedora' || loginOutput.userData.role === 'coordenadora') {
            navigate('/home');
          } else if (loginOutput.userData.role === 'cliente') {
            navigate('/home-cliente');
          } else {
            navigate('/nova-venda');
          }
        } else {
          navigate('/authentication/side-login');
        }
      } else {
        hideSpinner();
        await Swal.fire({
          icon: response.message?.indexOf('analisando') >= 0 ? 'info' : 'error',
          title: 'Ops...',
          text: response.message || 'Erro ao registrar',
          confirmButtonText: 'Ok'
        });
        const dadosUsados = {
          ...formData,
          senha: '*******',
        }
        logData.errorMessage += (response.message || 'Erro desconhecido') + ' \n > Dados: <pre>' + JSON.stringify(dadosUsados) + '</pre>';
      }

      if (logData.errorMessage) {
        telegramLogService.send(true, logData.errorMessage, new Date());
      }
    } catch (error: any) {
      hideSpinner();
      console.error('Erro ao registrar:', error);
      toast.error(error.message || 'Erro ao realizar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <LoadingSpinner isLoading={isLoading} title="Aguarde" message="Carregando..." />
      
      {/* Lado esquerdo - Imagem */}
      <div className="hidden lg:flex lg:w-7/12 xl:w-8/12 bg-[#ece1cf] from-primary to-primary-hover p-6">
        <div className="flex flex-col justify-center items-center w-full">
          <img 
            src="/v2/logos/logo_x.png" 
            alt="Logo X do Infinito" 
            className="w-80 mb-8"
          />
          <img 
            src="/v2/logos/login-bg.svg" 
            alt="Login" 
            className="max-w-md"
          />
        </div>
      </div>

      {/* Lado direito - Formulário */}
      <div className="flex-1 lg:w-5/12 xl:w-4/12 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          {/* Header */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img
              src="/v2/logos/logo_x.png"
              alt="Logo X do Infinito"
              className="w-60"
            />
          </div>
          <div className="text-center lg:text-left">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Primeiro acesso
            </h1>
            <p className="text-sm text-gray-600">Informe seus dados para aprovarmos o seu cadastro</p>
          </div>

          {/* Formulário */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Seleção de franquia */}
            <div className="space-y-2">
              <Label htmlFor="franquia" className="text-sm font-semibold">
                Selecione sua franquia
              </Label>
              <Select 
                value={franquiaSelecionada || ''} 
                onValueChange={onFranquiaChange}
                disabled={errorLoadFranchises || franquiasDisponiveis.length === 0}
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione sua franquia" />
                </SelectTrigger>
                <SelectContent>
                  {franquiasDisponiveis.map((franquia) => (
                    <SelectItem key={franquia.subdomain} value={franquia.subdomain}>
                      {franquia.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Verificação de documento - Primeiro passo */}
            {showDocumentCheck && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="documento" className="text-sm font-semibold">
                    CPF/CNPJ
                  </Label>
                  <Input
                    id="documento"
                    type="text"
                    placeholder="999.999.999-99"
                    value={documentCheck}
                    onChange={(e) => handleDocumentCheckChange(e.target.value)}
                    maxLength={18}
                    className="w-full"
                  />
                </div>
                <Button 
                  type="button"
                  onClick={checkPreRegister}
                  className="w-full"
                >
                  Continuar
                </Button>
              </div>
            )}

            {/* Formulário completo - Segundo passo */}
            {showForm && (
              <div className="space-y-4">
                {/* Nome/Razão Social */}
                <div className="space-y-2">
                  <Label htmlFor="nome" className="text-sm font-semibold">
                    Nome/Razão Social
                  </Label>
                  <Input
                    id="nome"
                    type="text"
                    placeholder="Nome completo ou razão social"
                    value={formData.nome_razao_social}
                    onChange={(e) => handleFormChange('nome_razao_social', e.target.value)}
                    required
                    className="w-full"
                  />
                </div>

                {/* CPF/CNPJ (readonly) */}
                <div className="space-y-2">
                  <Label htmlFor="cpf" className="text-sm font-semibold">
                    CPF/CNPJ
                  </Label>
                  <Input
                    id="cpf"
                    type="text"
                    placeholder="999.999.999-99"
                    value={formData.cpf_cnpj}
                    readOnly
                    className="w-full bg-gray-100 cursor-not-allowed"
                  />
                </div>

                {/* Telefone */}
                <div className="space-y-2">
                  <Label htmlFor="telefone" className="text-sm font-semibold">
                    Telefone/WhatsApp
                  </Label>
                  <Input
                    id="telefone"
                    type="tel"
                    placeholder="(99) 99999-9999"
                    value={formData.telefone}
                    onChange={(e) => handleFormChange('telefone', e.target.value)}
                    maxLength={15}
                    required
                    className="w-full"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold">
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Digite seu melhor email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    required
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">Seu melhor e-mail! Será usado para recuperar e acessar sua conta.</p>
                </div>

                {/* Senha */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold">
                    Crie sua senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="*********"
                      value={formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      required
                      className="w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Estado e Cidade */}
                <div className="grid grid-cols-5 gap-2">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="estado" className="text-sm font-semibold">
                      Estado
                    </Label>
                    <Select 
                      value={formData.estado}
                      onValueChange={(value) => handleFormChange('estado', value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione UF" />
                      </SelectTrigger>
                      <SelectContent>
                        {estados.map((estado: any) => (
                          <SelectItem key={estado.sigla} value={estado.sigla}>
                            {estado.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-3 space-y-2">
                    <Label htmlFor="cidade" className="text-sm font-semibold">
                      Cidade
                    </Label>
                    <div className="relative">
                      <Input
                        id="cidade"
                        type="text"
                        placeholder="Digite ou selecione a cidade"
                        value={pesquisaCidade}
                        onChange={(e) => handlePesquisaCidade(e.target.value)}
                        onFocus={() => setMostrarDropdownCidades(true)}
                        className="w-full pr-8"
                        autoComplete="off"
                      />
                      
                      {/* Dropdown com cidades filtradas */}
                      {mostrarDropdownCidades && cidades.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                          {cidadesFiltradas.length > 0 ? (
                            cidadesFiltradas.map((cidade) => (
                              <button
                                key={cidade}
                                type="button"
                                onClick={() => handleSelecionarCidade(cidade)}
                                className="w-full text-left px-4 py-2 hover:bg-blue-50 transition text-sm text-gray-800 border-b border-gray-100 last:border-b-0"
                              >
                                {cidade}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-center text-sm text-gray-500">
                              Nenhuma cidade encontrada
                            </div>
                          )}
                        </div>
                      )}

                      {/* Mensagem quando não há cidades carregadas */}
                      {mostrarDropdownCidades && cidades.length === 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 px-4 py-3 text-center text-sm text-gray-500">
                          Selecione um estado primeiro
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pergunta sobre código de indicação - Primeiro passo */}
                {mostrarPerguntaIndicacao && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">
                      Você foi indicado por alguém?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleRespostaPerguntaIndicacao(true)}
                        className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition font-medium text-sm"
                      >
                        Sim
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRespostaPerguntaIndicacao(false)}
                        className="flex-1 px-3 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition font-medium text-sm"
                      >
                        Não
                      </button>
                    </div>
                  </div>
                )}

                {/* Formulário de indicação */}
                {mostrarFormIndicacao && (
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-gray-800">
                        Código de Indicação
                      </Label>
                      <button
                        type="button"
                        onClick={()=>{handleRespostaPerguntaIndicacao(false)}}
                        className="text-gray-500 hover:text-gray-700 text-sm underline"
                      >
                        Não tenho
                      </button>
                    </div>

                    <Input
                      type="number"
                      placeholder="Digite o código"
                      value={codigoIndicacao}
                      onChange={(e) => onCodigoIndicacaoChange(e.target.value)}
                      className="w-full"
                    />

                    {/* Mensagem de sucesso */}
                    {codigoIndicacaoValido === true && nomeIndicante && (
                      <div className="bg-green-100 border border-green-300 rounded p-2 flex items-center gap-2">
                        <span className="text-lg">✓</span>
                        <p className="text-sm text-green-800">
                          Ótimo! Indicação por: <strong>{nomeIndicante}</strong>
                        </p>
                      </div>
                    )}

                    {/* Mensagem de erro */}
                    {codigoIndicacaoValido === false && codigoIndicacao.length > 0 && (
                      <div className="bg-red-100 border border-red-300 rounded p-2">
                        <p className="text-sm text-red-800">
                          Código não encontrado. Verifique e tente novamente.
                        </p>
                      </div>
                    )}

                    {/* Botão de verificação */}
                    {codigoIndicacaoValido !== true && (
                      <button
                        type="button"
                        onClick={() => verificarCodigoIndicacao(codigoIndicacao)}
                        disabled={verificandoCodigo || codigoIndicacao.length === 0}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {verificandoCodigo ? 'Verificando...' : 'Verificar Código'}
                      </button>
                    )}
                  </div>
                )}

                {/* Texto para reabrir a indicação (quando usuário respondeu não) */}
                {!mostrarPerguntaIndicacao && !mostrarFormIndicacao && usuarioTemIndicacao === false && (
                  <div className="text-center">
                    <p className="text-xs text-gray-600">
                      Mudou de ideia?{' '}
                      <button
                        type="button"
                        onClick={abrirFormIndicacaoNovamente}
                        className="text-primary hover:underline font-semibold"
                      >
                        Informe seu código de indicação
                      </button>
                    </p>
                  </div>
                )}

                {/* Termos de uso */}
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="termos"
                    checked={formData.aceite_termos}
                    onCheckedChange={(checked) => 
                      handleFormChange('aceite_termos', checked)
                    }
                    className="mt-1"
                  />
                  <label htmlFor="termos" className="text-sm text-gray-700">
                    Li e aceito os{' '}
                    <a 
                      href="https://xdoinfinitto.com.br/assets/Termos de Uso.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      termos de uso
                    </a>
                    , termos do{' '}
                    <a 
                      href="https://xdoinfinitto.com.br/assets/Termos de Uso e Regulamento - Programa de Benefícios.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      programa de benefícios
                    </a>{' '}
                    e termos do{' '}
                    <a 
                      href="https://xdoinfinitto.com.br/assets/Termos e Regulamento Programa de Indicacao e Rede.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      programa de indicação e rede
                    </a>
                    .
                  </label>
                </div>

                {/* Botão de envio */}
                <Button 
                  type="submit"
                  disabled={!formData.aceite_termos || submitting}
                  className="w-full"
                >
                  {submitting ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            )}
          </form>

          {/* Links adicionais */}
          <div className="space-y-3 text-center text-sm">
            <p>
              Já tem uma conta?{' '}
              <a 
                href="/v2/authentication/side-login" 
                className="text-primary hover:underline font-medium"
              >
                Entrar
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideRegister;
